import { z } from 'zod';

/**
 * SIL-952 — per-SKU comp coverage DTO, powering GET /api/sku-comp-coverage.
 *
 * Reads sku_comp_coverage (db/schema/838_sku_comp_coverage.sql), the table
 * the ingestion orchestrator's persistSkuCompCoverage() recomputes on every
 * cron run — one row per live SKU. This is the measured SLO the ops
 * coverage page renders (distribution + below-grade list) and the
 * discoverEligibleMarketCompSkus scheduler steers on.
 *
 * coverageGrade reuses confidenceFor()'s vocabulary verbatim (apps/api/src/
 * services/fmv/match-and-price.ts) — 'high' | 'medium' | 'low', n>=5 / n>=2
 * / else, applied to usedBandCompCount. Do not add a fourth confidence
 * vocabulary here.
 */
export const skuCompCoverageItemSchema = z.object({
  skuId: z.string().uuid(),
  verifiedSku: z.string().nullable(),
  modelName: z.string().nullable(),
  manufacturerName: z.string(),
  assetClassId: z.string().nullable(),
  assetClassName: z.string().nullable(),
  usedBandCompCount: z.number().int(),
  totalCompCount: z.number().int(),
  distinctSourceCount: z.number().int(),
  newestObservedAt: z.string().nullable(),
  coverageGrade: z.enum(['high', 'medium', 'low']),
  computedAt: z.string(),
});
export type SkuCompCoverageItem = z.infer<typeof skuCompCoverageItemSchema>;

/** Per-grade counts across the whole live-SKU population — the distribution
 * the ops page's summary strip renders. */
export const skuCompCoverageSummarySchema = z.object({
  total: z.number().int(),
  high: z.number().int(),
  medium: z.number().int(),
  low: z.number().int(),
});
export type SkuCompCoverageSummary = z.infer<typeof skuCompCoverageSummarySchema>;

/** Response for GET /api/sku-comp-coverage. `items` is the below-grade list
 * (coverageGrade != 'high'), ordered worst grade first then by
 * usedBandCompCount ascending — `summary` covers every live SKU regardless
 * of grade, so the two counts are NOT expected to reconcile 1:1
 * (summary.total = summary.high + summary.medium + summary.low, while
 * items.length = summary.medium + summary.low). */
export const skuCompCoverageResponseSchema = z.object({
  summary: skuCompCoverageSummarySchema,
  items: z.array(skuCompCoverageItemSchema),
});
export type SkuCompCoverageResponse = z.infer<typeof skuCompCoverageResponseSchema>;
